// /app/frontend/src/pages/admin/SuperAdminMFAPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Shield, ShieldCheck, ShieldOff, Search, RotateCcw,
  Users, AlertTriangle, Loader2, Settings
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import api from '../../services/api';

export default function SuperAdminMFAPage() {
  const [users, setUsers] = useState([]);
  const [policy, setPolicy] = useState({ require_mfa_for_dpo: false, require_mfa_for_all: false });
  const [loading, setLoading] = useState(true);
  const [policyLoading, setPolicyLoading] = useState(false);
  const [search, setSearch] = useState('');

  // Reset confirmation
  const [confirmUser, setConfirmUser] = useState(null);
  const [resetting, setResetting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, policyRes] = await Promise.all([
        api.get('/super-admin/mfa/users'),
        api.get('/super-admin/mfa/policy').catch(() => ({ data: { require_mfa_for_dpo: false, require_mfa_for_all: false } })),
      ]);
      setUsers(usersRes.data);
      setPolicy(policyRes.data);
    } catch { toast.error('Failed to load MFA data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleResetMFA = async () => {
    if (!confirmUser) return;
    setResetting(true);
    try {
      await api.post(`/super-admin/mfa/users/${confirmUser.id}/reset`);
      toast.success(`MFA reset for ${confirmUser.email}`);
      setConfirmUser(null);
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset MFA');
    } finally { setResetting(false); }
  };

  const handlePolicyChange = async (field, value) => {
    const newPolicy = { ...policy, [field]: value };
    setPolicy(newPolicy);
    setPolicyLoading(true);
    try {
      await api.put('/super-admin/mfa/policy', newPolicy);
      toast.success('MFA policy updated');
    } catch {
      toast.error('Failed to update policy');
      setPolicy(policy); // rollback
    } finally { setPolicyLoading(false); }
  };

  const filtered = users.filter(u =>
    !search || u.email.toLowerCase().includes(search.toLowerCase()) ||
    u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    u.org_name?.toLowerCase().includes(search.toLowerCase())
  );

  const stats = {
    total: users.length,
    mfaEnabled: users.filter(u => u.mfa_enabled).length,
    mfaDisabled: users.filter(u => !u.mfa_enabled).length,
    dpoUsers: users.filter(u => u.role === 'dpo' || u.role === 'org_owner').length,
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Shield className="h-6 w-6 text-violet-400" />
            MFA Administration
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Manage two-factor authentication across the platform
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Total Users', value: stats.total, icon: Users, color: 'text-slate-400' },
            { label: 'MFA Enabled', value: stats.mfaEnabled, icon: ShieldCheck, color: 'text-emerald-400' },
            { label: 'MFA Disabled', value: stats.mfaDisabled, icon: ShieldOff, color: 'text-rose-400' },
            { label: 'DPO / Org Owners', value: stats.dpoUsers, icon: Shield, color: 'text-violet-400' },
          ].map(s => (
            <Card key={s.label} className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">{s.label}</p>
                  <p className="text-2xl font-bold text-white mt-0.5">{s.value}</p>
                </div>
                <s.icon className={cn("h-8 w-8 opacity-60", s.color)} />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Policy Settings */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Settings className="h-5 w-5 text-violet-400" />
              Platform MFA Policy
              {policyLoading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
            </CardTitle>
            <CardDescription>
              Configure platform-wide MFA requirements. Users who don't comply will be prompted on next login.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800/40 border border-slate-700">
              <div>
                <Label className="text-white font-medium">Require MFA for DPO users</Label>
                <p className="text-sm text-slate-400 mt-0.5">
                  Enforce MFA for all DPO and Org Owner accounts
                </p>
              </div>
              <Switch
                checked={policy.require_mfa_for_dpo}
                onCheckedChange={v => handlePolicyChange('require_mfa_for_dpo', v)}
                disabled={policyLoading}
              />
            </div>
            <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800/40 border border-slate-700">
              <div>
                <Label className="text-white font-medium">Require MFA for all users</Label>
                <p className="text-sm text-slate-400 mt-0.5">
                  Enforce MFA for every user on the platform
                </p>
              </div>
              <Switch
                checked={policy.require_mfa_for_all}
                onCheckedChange={v => handlePolicyChange('require_mfa_for_all', v)}
                disabled={policyLoading}
              />
            </div>
            {(policy.require_mfa_for_dpo || policy.require_mfa_for_all) && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 text-sm text-amber-400">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>Policy enforcement is informational — users are notified but not blocked until a login enforcement hook is added.</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white">User MFA Status</CardTitle>
                <CardDescription>View and manage MFA for all platform users</CardDescription>
              </div>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search users..."
                  className="pl-9 bg-slate-900 border-slate-700"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Organisation</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>MFA Status</TableHead>
                    <TableHead>Enabled On</TableHead>
                    <TableHead className="w-24">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : filtered.map(u => (
                    <TableRow key={u.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-white text-sm">{u.full_name || '—'}</p>
                          <p className="text-xs text-slate-400">{u.email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-300">{u.org_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize text-xs">{u.role?.replace('_', ' ')}</Badge>
                      </TableCell>
                      <TableCell>
                        {u.mfa_enabled ? (
                          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 gap-1">
                            <ShieldCheck className="h-3 w-3" /> Enabled
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-slate-500/10 text-slate-400 border-slate-500/20 gap-1">
                            <ShieldOff className="h-3 w-3" /> Disabled
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-400">
                        {u.mfa_setup_at ? new Date(u.mfa_setup_at).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell>
                        {u.mfa_enabled && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-amber-400 border-amber-500/30 hover:bg-amber-500/10 text-xs"
                            onClick={() => setConfirmUser(u)}
                          >
                            <RotateCcw className="h-3 w-3 mr-1" />
                            Reset
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Confirm Reset Dialog */}
      <Dialog open={!!confirmUser} onOpenChange={() => setConfirmUser(null)}>
        <DialogContent className="bg-slate-950 border-slate-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-400" />
              Reset MFA for User
            </DialogTitle>
            <DialogDescription>
              This will disable MFA for <strong className="text-white">{confirmUser?.email}</strong>.
              They will need to set up MFA again on their next login.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setConfirmUser(null)} disabled={resetting}>
              Cancel
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              onClick={handleResetMFA}
              disabled={resetting}
            >
              {resetting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
              Confirm Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
